import { useState, useMemo, useRef, useCallback } from 'react';
import { Dialog, DialogProps as MuiDialogProps } from '@mui/material';

type InternalProps<TResult> = {
  open: boolean;
  onClose: (data?: TResult) => void;
};

type DialogProps<TProps extends object, TResult> = InternalProps<TResult> & TProps;
type DialogComponent<TProps extends object, TResult> = React.FC<DialogProps<TProps, TResult>>;

interface WithModalDialogOptions extends Partial<MuiDialogProps> {
  backdrop?: boolean;
}

export function withModalDialog<TProps extends object, TResult>(Component: React.ComponentType<DialogProps<TProps, TResult>>, dialogProps: WithModalDialogOptions = {}): DialogComponent<TProps, TResult> {
  return (props: DialogProps<TProps, TResult>) => {
    const { open, onClose, ...componentProps } = props;

    const handleClose = useCallback((_: any, reason: string) => {
      if (!(reason === 'backdropClick' && !dialogProps.backdrop)) {
        onClose();
      }
    }, [onClose]);

    return <Dialog open={open} onClose={handleClose} {...dialogProps}>
      <Component {...componentProps as DialogProps<TProps, TResult>} onClose={onClose} />
    </Dialog>
  };
}

type ExtractProps<T> = T extends DialogComponent<infer P extends object, any> ? P : never;
type ExtractResult<T> = T extends DialogComponent<any, infer R> ? R : never;

interface UseModalDialogResult<TProps extends object, TResult> {
  register: () => DialogProps<TProps, TResult>;
  open: (props: TProps) => Promise<TResult | undefined>;
}

export function useModalDialog<T extends DialogComponent<any, any>>(): UseModalDialogResult<ExtractProps<T>, ExtractResult<T>> {
  const [open, setOpen] = useState<boolean>(false);
  const [props, setProps] = useState<ExtractProps<T>>();
  const promiseRef = useRef<((value: ExtractResult<T> | undefined) => void) | null>(null);

  function handleClose(data?: ExtractResult<T>) {
    setOpen(false);
    promiseRef.current?.(data);
    promiseRef.current = null;
  }

  return useMemo(() => ({
    register: () => ({ open, onClose: handleClose, ...props } as DialogProps<ExtractProps<T>, ExtractResult<T>>),
    open: async (dialogProps: ExtractProps<T>) => {
      return new Promise<ExtractResult<T> | undefined>((resolve) => {
        promiseRef.current = resolve;
        setProps(dialogProps);
        setOpen(true);
      });
    }
  }), [open]);
}